import React, { useState } from 'react';
import { Upload, X, RefreshCw } from 'lucide-react';
import { templateUtils } from '../utils/templateUtils';
import { fileAttachmentUtils } from '../utils/fileAttachmentUtils';

/**
 * Reusable File Attachment Component
 * Used in both EmailTemplates and CampaignModal
 */
const FileAttachmentSection = ({
  attachments = [],
  uploadingFile = false,
  onFileSelect,
  onRemoveAttachment,
  disabled = false,
  label = "File Attachments",
  showLabel = true,
  compact = false,
  readOnly = false
}) => {
  const inputId = `file-upload-${Math.random().toString(36).substr(2, 9)}`;

  // Drag and drop state
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !readOnly) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragActive to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragActive(false);
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !readOnly) {
      setIsDragOver(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    setIsDragOver(false);

    if (disabled || readOnly || !onFileSelect) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Create a synthetic event to match the onChange handler
      const syntheticEvent = {
        target: {
          files: files
        }
      };
      onFileSelect(syntheticEvent);
    }
  };

  return (
    <div>
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {readOnly && <span className="text-xs text-gray-500">(from template)</span>}
        </label>
      )}

      {/* File Upload Area - Hidden in read-only mode */}
      {!readOnly && (
        <div
          className={`border-2 border-dashed rounded-lg transition-colors ${
            isDragOver
              ? 'border-blue-400 bg-blue-50'
              : isDragActive
                ? 'border-blue-300 bg-blue-25'
                : 'border-gray-300 hover:border-gray-400'
          } ${compact ? 'p-3' : 'p-4'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id={inputId}
            multiple
            onChange={onFileSelect}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg,.zip,.rar,.7z,.json,.xml,.html"
            disabled={disabled}
          />
          <label
            htmlFor={inputId}
            className={`cursor-pointer flex flex-col items-center justify-center space-y-2 ${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <Upload
              size={compact ? 20 : 24}
              className={`${
                isDragOver
                  ? 'text-blue-500'
                  : isDragActive
                    ? 'text-blue-400'
                    : 'text-gray-400'
              }`}
            />
            <span className={`${compact ? 'text-xs' : 'text-sm'} ${
              isDragOver
                ? 'text-blue-600 font-medium'
                : isDragActive
                  ? 'text-blue-500'
                  : 'text-gray-600'
            }`}>
              {uploadingFile
                ? 'Uploading...'
                : isDragOver
                  ? 'Drop files here!'
                  : 'Click to upload files or drag and drop'
              }
            </span>
            <span className="text-xs text-gray-500">
              PDF, DOC, XLS, PPT, Images, Archives (Max 25MB total)
            </span>
          </label>
        </div>
      )}

      {/* Attachment List */}
      {attachments.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>
            Attached Files ({attachments.length}):
          </p>
          <div className="space-y-2">
            {attachments.map((attachment) => {
              const display = templateUtils.getAttachmentDisplay(attachment);
              return (
                <div key={attachment.id} className={`flex items-center justify-between bg-gray-50 rounded border ${
                  compact ? 'p-2' : 'p-2'
                }`}>
                  <div className="flex items-center space-x-2">
                    <span className={compact ? 'text-base' : 'text-lg'}>{display.icon}</span>
                    <div>
                      <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>
                        {display.name}
                      </p>
                      <p className="text-xs text-gray-500">{display.size}</p>
                    </div>
                  </div>
                  {onRemoveAttachment && !disabled && !readOnly && (
                    <button
                      type="button"
                      onClick={() => onRemoveAttachment(attachment.id)}
                      className="text-gray-400 hover:text-red-600 p-1"
                      title="Remove attachment"
                    >
                      <X size={compact ? 12 : 16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-xs text-gray-500">
            Total size: {fileAttachmentUtils.formatFileSize(fileAttachmentUtils.getTotalAttachmentSize(attachments))} / 25MB
          </div>
        </div>
      )}

      {uploadingFile && (
        <div className={`mt-2 ${compact ? 'text-xs' : 'text-sm'} text-gray-500 flex items-center gap-2`}>
          <RefreshCw className="animate-spin" size={compact ? 12 : 14} />
          Uploading file...
        </div>
      )}
    </div>
  );
};

export default FileAttachmentSection;